import { useMemo, type ReactNode } from 'react';
import {
  CartesianGrid,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  type TooltipProps,
} from 'recharts';
import { cssVar, palette } from './chartTheme';
import type { QuadrantPoint } from '@/api/bi/types';

/**
 * `QuadrantChart` — BCG-style 2x2 bubble plot for profitability triage.
 *
 *      Y (margin Δ vs sub-cat median)
 *          ▲
 *  +pp  ──┼──┼──   STARS              CASH COWS
 *          │
 *  0   ────┼──┼──  ─────────────────────────────────────────
 *          │
 *  -pp  ──┼──┼──   QUESTION MARKS     DOGS
 *          │
 *          └──────────────────────────► X (sales share %)
 *           low                       high
 *
 *  Bubble radius (Z) = gross profit ₹.
 *
 *  X axis is the bubble's share of the active scope; the median across
 *  points splits "low" vs "high" share. Y is the percentage-point gap to
 *  the caller's chosen median margin.
 */

export interface QuadrantChartProps {
  points: QuadrantChartPoint[];
  /** Median (used as the X axis split). */
  shareSplit?: number;
  /** Y axis split — defaults to 0 since the helper already centers on the median. */
  marginSplit?: number;
  height?: number | string;
  /** Called when the user clicks a bubble. */
  onPointClick?: (p: QuadrantChartPoint) => void;
  /** Pretty NPR formatter for the bubble tooltip. */
  formatNpr?: (n: number) => string;
  xLabel?: string;
  yLabel?: string;
  zLabel?: string;
}

export interface QuadrantChartPoint {
  id?: number | string;
  label?: string;
  vendor_id?: QuadrantPoint['vendor_id'];
  vendor_name?: QuadrantPoint['vendor_name'];
  x: number;
  y: number;
  z: number;
  decision?: QuadrantPoint['decision'];
  composite_score?: QuadrantPoint['composite_score'];
}

const DECISION_COLORS: Record<string, string> = {
  maximize: 'hsl(142 71% 45%)',     // green
  minimize: 'hsl(0 84% 60%)',       // red
  neutral:  'hsl(215 16% 56%)',     // grey
};

function PointTooltip({
  active,
  payload,
  formatNpr,
  xLabel,
  yLabel,
  zLabel,
}: TooltipProps<number, string> & {
  formatNpr?: (n: number) => string;
  xLabel: string;
  yLabel: string;
  zLabel: string;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload as QuadrantChartPoint | undefined;
  if (!p) return null;
  const label = p.label ?? p.vendor_name ?? (p.id != null ? String(p.id) : 'Unknown');
  return (
    <div className="rounded-md border border-border bg-surface-elevated px-3 py-2 text-xs shadow-soft">
      <p className="mb-1 font-medium text-foreground">{label}</p>
      <ul className="space-y-1">
        <Row label={xLabel} value={`${(p.x * 100).toFixed(1)}%`} />
        <Row
          label={yLabel}
          value={`${(p.y * 100 >= 0 ? '+' : '')}${(p.y * 100).toFixed(1)} pp`}
        />
        <Row
          label={zLabel}
          value={formatNpr ? formatNpr(p.z) : String(Math.round(p.z))}
        />
        {p.composite_score != null && (
          <Row label="Score" value={p.composite_score.toFixed(0)} />
        )}
        {p.decision && (
          <Row
            label="Decision"
            value={
              <span
                className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
                style={{
                  backgroundColor: `${DECISION_COLORS[p.decision]}1A`,
                  color: DECISION_COLORS[p.decision],
                }}
              >
                {p.decision}
              </span>
            }
          />
        )}
      </ul>
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="kpi-num font-medium">{value}</span>
    </li>
  );
}

export function QuadrantChart({
  points,
  shareSplit,
  marginSplit = 0,
  height = 320,
  onPointClick,
  formatNpr,
  xLabel = 'Sales share',
  yLabel = 'Margin Δ vs median',
  zLabel = 'Gross profit',
}: QuadrantChartProps) {
  // Compute domains so bubbles aren't clipped by axis edges. We pad 10 %
  // around the extents and ensure the splits live inside the domain.
  const { xDomain, yDomain, zDomain, defaultShareSplit } = useMemo(() => {
    if (!points.length) {
      return {
        xDomain: [0, 1] as [number, number],
        yDomain: [-0.2, 0.2] as [number, number],
        zDomain: [10, 200] as [number, number],
        defaultShareSplit: 0.5,
      };
    }
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const zs = points.map((p) => Math.max(0, p.z));
    const minY = Math.min(...ys, marginSplit);
    const maxY = Math.max(...ys, marginSplit);
    const padY = Math.max(0.02, (maxY - minY) * 0.1);
    const sortedXs = [...xs].sort((a, b) => a - b);
    const median = sortedXs.length % 2
      ? sortedXs[(sortedXs.length - 1) / 2]
      : (sortedXs[sortedXs.length / 2 - 1] + sortedXs[sortedXs.length / 2]) / 2;
    return {
      xDomain: [0, Math.max(1, Math.max(...xs) * 1.1)] as [number, number],
      yDomain: [minY - padY, maxY + padY] as [number, number],
      zDomain: [Math.min(...zs), Math.max(...zs, 1)] as [number, number],
      defaultShareSplit: median,
    };
  }, [points, marginSplit]);

  const split = shareSplit ?? defaultShareSplit;

  // Group bubbles by decision so legend swatches can stay distinct.
  const fillFor = (p: QuadrantChartPoint): string => {
    if (p.decision && DECISION_COLORS[p.decision]) return DECISION_COLORS[p.decision];
    return palette.primary();
  };

  return (
    <div className="relative" style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <ScatterChart
          margin={{ top: 16, right: 24, left: 0, bottom: 24 }}
          onClick={(e: { activePayload?: Array<{ payload: QuadrantChartPoint }> }) => {
            const p = e.activePayload?.[0]?.payload;
            if (p && onPointClick) onPointClick(p);
          }}
        >
          <CartesianGrid strokeDasharray="2 4" stroke={palette.grid()} />

          {/* Quadrant background tints — green TL/TR, red BL/BR — softer than the bubbles. */}
          <ReferenceArea
            x1={xDomain[0]}
            x2={split}
            y1={marginSplit}
            y2={yDomain[1]}
            fill={cssVar('success', 0.05)}
            stroke="none"
          />
          <ReferenceArea
            x1={split}
            x2={xDomain[1]}
            y1={marginSplit}
            y2={yDomain[1]}
            fill={cssVar('success', 0.08)}
            stroke="none"
          />
          <ReferenceArea
            x1={xDomain[0]}
            x2={split}
            y1={yDomain[0]}
            y2={marginSplit}
            fill={cssVar('warning', 0.04)}
            stroke="none"
          />
          <ReferenceArea
            x1={split}
            x2={xDomain[1]}
            y1={yDomain[0]}
            y2={marginSplit}
            fill={cssVar('danger', 0.06)}
            stroke="none"
          />

          <ReferenceLine x={split} stroke={palette.axis()} strokeDasharray="3 3" />
          <ReferenceLine y={marginSplit} stroke={palette.axis()} strokeDasharray="3 3" />

          <XAxis
            type="number"
            dataKey="x"
            domain={xDomain}
            stroke={palette.axis()}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
            label={{
              value: xLabel,
              position: 'insideBottom',
              offset: -8,
              style: { fontSize: 11, fill: palette.axis() },
            }}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={yDomain}
            stroke={palette.axis()}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            tickFormatter={(v: number) => `${v >= 0 ? '+' : ''}${Math.round(v * 100)}pp`}
            label={{
              value: yLabel,
              angle: -90,
              position: 'insideLeft',
              offset: 12,
              style: { fontSize: 11, fill: palette.axis() },
            }}
          />
          <ZAxis type="number" dataKey="z" range={[60, 600]} domain={zDomain} />

          <Tooltip
            cursor={{ stroke: palette.axis(), strokeDasharray: '3 3' }}
            content={(p) => (
              <PointTooltip
                {...(p as TooltipProps<number, string>)}
                formatNpr={formatNpr}
                xLabel={xLabel}
                yLabel={yLabel}
                zLabel={zLabel}
              />
            )}
          />

          <Scatter
            data={points}
            isAnimationActive={false}
            shape="circle"
          >
            {points.map((p, i) => (
              <Cell key={i} fill={fillFor(p)} fillOpacity={0.65} stroke={fillFor(p)} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>

      {/* Corner labels — drawn outside the SVG so Recharts doesn't clip them. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-8 top-2 grid grid-cols-2 grid-rows-2 px-6 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <span className="self-start text-left">Star</span>
        <span className="self-start text-right">Cash cow</span>
        <span className="self-end text-left">Question mark</span>
        <span className="self-end text-right">Dog</span>
      </div>
    </div>
  );
}
